<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue'

const slides = [
  {
    image: '/slides/slide1.jpg',
    title: 'Modern Point of Sale',
    description: 'Streamline your sales with our intuitive POS system',
    color: 'from-blue-600 to-blue-400'
  },
  {
    image: '/slides/slide2.jpg',
    title: 'Inventory Management',
    description: 'Keep track of your stock in real-time',
    color: 'from-green-600 to-green-400'
  },
  {
    image: '/slides/slide3.jpg',
    title: 'Financial Analytics',
    description: 'Make data-driven decisions with powerful insights',
    color: 'from-purple-600 to-purple-400'
  }
]

const currentSlide = ref(0)
const autoplayInterval = ref<number | null>(null)

const nextSlide = () => {
  currentSlide.value = (currentSlide.value + 1) % slides.length
}

const prevSlide = () => {
  currentSlide.value = currentSlide.value === 0 ? slides.length - 1 : currentSlide.value - 1
}

const startAutoplay = () => {
  autoplayInterval.value = window.setInterval(nextSlide, 5000)
}

const stopAutoplay = () => {
  if (autoplayInterval.value) {
    clearInterval(autoplayInterval.value)
    autoplayInterval.value = null
  }
}

onMounted(() => {
  startAutoplay()
})

onBeforeUnmount(() => {
  stopAutoplay()
})
</script>

<template>
  <div 
    class="relative h-[500px] overflow-hidden rounded-2xl"
    @mouseenter="stopAutoplay"
    @mouseleave="startAutoplay"
  >
    <!-- Slides -->
    <div 
      v-for="(slide, index) in slides" 
      :key="index"
      class="absolute inset-0 transition-opacity duration-500"
      :class="{ 'opacity-0': currentSlide !== index }"
    >
      <!-- Gradient Overlay -->
      <div 
        class="absolute inset-0 bg-gradient-to-r opacity-90"
        :class="slide.color"
      ></div>
      
      <!-- Content -->
      <div class="relative h-full flex items-center justify-center text-white p-8">
        <div class="text-center max-w-2xl">
          <h2 class="text-4xl font-bold mb-4">{{ slide.title }}</h2>
          <p class="text-xl">{{ slide.description }}</p>
        </div>
      </div>
    </div>

    <!-- Navigation Buttons -->
    <button 
      @click="prevSlide"
      class="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white backdrop-blur-sm transition-colors"
    >
      ←
    </button>
    <button 
      @click="nextSlide"
      class="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white backdrop-blur-sm transition-colors"
    >
      →
    </button>

    <!-- Indicators -->
    <div class="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
      <button
        v-for="(_, index) in slides"
        :key="index"
        @click="currentSlide = index"
        class="w-2 h-2 rounded-full transition-all"
        :class="[
          currentSlide === index
            ? 'bg-white w-6'
            : 'bg-white/50 hover:bg-white/75'
        ]"
      />
    </div>
  </div>
</template>